import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import ForceGraph2D from "react-force-graph-2d";
import { useNavigate } from "react-router-dom";
import { Loader2, Maximize } from "lucide-react";

const API = '/api';

export default function GraphPage() {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const containerRef = useRef();
  const graphRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/summaries`)
      .then(res => setSummaries(res.data))
      .catch(() => toast.error("FAILED TO LOAD DATA FOR GRAPH"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: window.innerHeight - 200 // Leave room for header
        });
      }
    };
    
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [loading]);

  const graphData = useMemo(() => {
    if (!summaries.length) return { nodes: [], links: [] };

    const nodes = [];
    const links = [];
    const topicNodesMap = new Map();

    // Create central "Uncategorized" topic if needed
    const uncategorizedId = "topic:Uncategorized";

    summaries.forEach(s => {
      // Create Summary Node
      nodes.push({
        id: `summary:${s.id}`,
        name: s.video_title,
        group: "summary",
        val: 1.5,
        originalId: s.id
      });

      let tags = s.tags || [];
      if (tags.length === 0) tags = ["Uncategorized"];

      tags.forEach(tag => {
        const topicId = `topic:${tag}`;
        
        // Create Topic Node if it doesn't exist
        if (!topicNodesMap.has(topicId)) {
          topicNodesMap.set(topicId, true);
          nodes.push({
            id: topicId,
            name: tag,
            group: "topic",
            val: 4 // Make topic nodes slightly bigger
          });
        }

        // Link Summary to Topic
        links.push({
          source: `summary:${s.id}`,
          target: topicId
        });
      });
    });

    return { nodes, links };
  }, [summaries]);

  const handleNodeClick = useCallback((node) => {
    if (node.group === "summary") {
      navigate(`/summary/${node.originalId}`);
    } else {
      // Zoom into topic node
      if (graphRef.current) {
        graphRef.current.centerAt(node.x, node.y, 1000);
        graphRef.current.zoom(3, 1000);
      }
    }
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Loader2 size={32} className="animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="anim-in r-full" style={{ maxWidth: 1200 }} data-testid="graph-page">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#CC0000", marginBottom: 6 }}>KNOWLEDGE / VISUALIZATION</div>
          <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2rem", fontWeight: 700, color: "#111", margin: 0, lineHeight: 1 }}>KNOWLEDGE GRAPH</h1>
          <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
        </div>
        <button 
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoomToFit(400);
            }
          }}
          className="ind-btn-ghost"
        >
          <Maximize size={14} style={{ marginRight: 6 }} /> FIT TO SCREEN
        </button>
      </div>

      <div style={{ fontFamily: "Inter, sans-serif", fontSize: "0.85rem", color: "#666", marginBottom: 24, maxWidth: 600 }}>
        An interactive, infinite-canvas view of your summaries clustered by AI-generated topics. <b>Scroll to zoom</b>, <b>drag to pan</b>, and <b>click</b> a node to view the summary or zoom into a topic.
      </div>

      <div 
        ref={containerRef}
        style={{ 
          background: "#fff", 
          border: "2px solid #111", 
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Corner marks */}
        <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000", zIndex: 10 }} />
        <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000", zIndex: 10 }} />
        <div style={{ position: "absolute", bottom: -3, left: -3, width: 6, height: 6, background: "#111", zIndex: 10 }} />
        <div style={{ position: "absolute", bottom: -3, right: -3, width: 6, height: 6, background: "#111", zIndex: 10 }} />

        {graphData.nodes.length > 0 ? (
          <ForceGraph2D
            ref={graphRef}
            width={dimensions.width}
            height={dimensions.height}
            graphData={graphData}
            nodeLabel="name"
            nodeColor={node => node.group === "topic" ? "#CC0000" : "#111111"}
            nodeRelSize={4}
            linkColor={() => "rgba(0,0,0,0.15)"}
            linkWidth={1.5}
            onNodeClick={handleNodeClick}
            d3AlphaDecay={0.02}
            d3VelocityDecay={0.3}
            nodeCanvasObject={(node, ctx, globalScale) => {
              const label = node.name;
              const fontSize = node.group === "topic" ? 14/globalScale : 10/globalScale;
              
              // Draw node circle
              ctx.beginPath();
              ctx.arc(node.x, node.y, node.group === "topic" ? 8 : 4, 0, 2 * Math.PI, false);
              ctx.fillStyle = node.group === "topic" ? "#CC0000" : "#111111";
              ctx.fill();

              // Draw label
              if (globalScale > 1.5 || node.group === "topic") {
                ctx.font = `600 ${fontSize}px 'Inter', sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = node.group === "topic" ? '#111' : '#555';
                
                // Add slight background to text for readability
                const textWidth = ctx.measureText(label).width;
                ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
                ctx.fillRect(node.x - textWidth/2 - 2, node.y + (node.group === "topic" ? 12 : 8) - fontSize/2, textWidth + 4, fontSize + 2);
                
                ctx.fillStyle = node.group === "topic" ? '#111' : '#555';
                ctx.fillText(label, node.x, node.y + (node.group === "topic" ? 12 : 8));
              }
            }}
          />
        ) : (
          <div style={{ height: dimensions.height, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.8rem", color: "#888", letterSpacing: "0.1em" }}>
            NO SUMMARIES TO GRAPH
          </div>
        )}
      </div>
    </div>
  );
}
